import { User } from '../types/profile';
import api from './api';
import { getToken } from '../utils/storage';
import cloudinaryService from './cloudinaryService';

/**
 * Délai maximal pour les requêtes API (en ms)
 */
const API_TIMEOUT = 10000;

/**
 * Interface pour la réponse réelle du backend /users/me
 * ✨ CORRIGÉ: Le backend retourne directement les données utilisateur (pas dans un objet "user")
 * Structure: { success: true, data: { id, email, givenName, familyName, financialInstitutionId, ... } }
 */
interface BackendUserData {
  id: string;
  email: string;
  emailVerified?: boolean;
  name?: string;
  givenName?: string | null;  // Le backend utilise givenName (pas firstName)
  familyName?: string | null; // Le backend utilise familyName (pas lastName)
  picture?: string | null;
  phone?: string | null;
  phoneVerified?: boolean;
  address?: string | null;
  idNumber?: string | null;
  idType?: string | null;
  idStatus?: 'verified' | 'pending' | 'rejected' | null;
  role?: string;
  bio?: string | null;
  userType?: 'sme' | 'financial_institution';
  companyId?: string | null;
  financialInstitutionId?: string | null;  // ✨ Directement dans l'objet
  isCompanyOwner?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * ✨ CORRIGÉ: Mappe la réponse backend vers le format User attendu par le frontend
 * Le backend retourne directement l'objet utilisateur, pas { user: {...} }
 */
function mapBackendResponseToUser(backendData: BackendUserData): User {
  // Construire le nom complet à partir de name, givenName ou familyName
  const fullName = backendData.name || 
    [backendData.givenName, backendData.familyName].filter(Boolean).join(' ').trim() || 
    backendData.email;
  
  console.log('🔄 Mapping utilisateur (user.ts):', {
    id: backendData.id,
    name: backendData.name,
    givenName: backendData.givenName,
    familyName: backendData.familyName,
    financialInstitutionId: backendData.financialInstitutionId,
    companyId: backendData.companyId,
    userType: backendData.userType
  });
  
  return {
    id: backendData.id,
    email: backendData.email,
    name: fullName,
    // ✨ Le backend utilise directement "financialInstitutionId"
    financialInstitutionId: backendData.financialInstitutionId || undefined,
    companyId: backendData.companyId || undefined,
    // ✨ userType provient du backend, ou est déduit des associations
    // Si aucune association, userType reste undefined (utilisateur n'a pas encore soumis de formulaire)
    userType: backendData.userType || 
              (backendData.financialInstitutionId ? 'financial_institution' : 
               backendData.companyId ? 'sme' : undefined),
    picture: backendData.picture || undefined,
    phone: backendData.phone || undefined,
    address: backendData.address || undefined,
    role: backendData.role,
    idNumber: backendData.idNumber || undefined,
    idStatus: backendData.idStatus || undefined,
    phoneVerified: backendData.phoneVerified,
    createdAt: backendData.createdAt || new Date().toISOString(),
    updatedAt: backendData.updatedAt,
  };
}

class UserService {
  /**
   * Récupère le profil de l'utilisateur connecté.
   * Combine les données d'Auth0 (localStorage) avec les données du backend.
   * Garantit qu'un échec de l'API ne bloque pas l'accès aux fonctionnalités.
   * @param forceRefresh - Force le rechargement des données depuis le serveur
   */
  async getProfile(forceRefresh = false): Promise<User> {
    // Récupérer immédiatement les données Auth0 du localStorage
    const storedAuth0User = localStorage.getItem('auth0_user');
    const auth0User = storedAuth0User ? JSON.parse(storedAuth0User) : {};
    
    // Construire un utilisateur de base à partir des données Auth0
    const baseUser: User = {
      id: auth0User.sub || auth0User.id || `local-${Date.now()}`,
      email: auth0User.email || 'N/A',
      name: auth0User.name || auth0User.nickname || auth0User.email || 'Utilisateur',
      picture: auth0User.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(auth0User.name || auth0User.email || 'U')}`,
      phone: auth0User.phone || 'N/A',
      address: auth0User.address || 'N/A',
      role: (auth0User.roles && auth0User.roles[0]) || auth0User['https://ksuit.app/roles']?.[0] || '',
      idNumber: auth0User.idNumber || 'N/A',
      idStatus: auth0User.idStatus || undefined,
      createdAt: auth0User.updated_at || new Date().toISOString(),
    };
    
    // Vérifier si nous avons un token d'accès pour appeler le backend
    const token = getToken();
    if (!token) {
      console.log('Pas de token disponible, utilisation des données Auth0 uniquement');
      return baseUser;
    }
    
    try {
      // Créer une promesse avec timeout pour éviter de bloquer l'UI
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout')), API_TIMEOUT);
      });
      
      // ✨ CORRIGÉ: Le backend retourne { success: true, data: {...utilisateur...} }
      const backendUserPromise = api.get<{ data: BackendUserData }>('/users/me', undefined, forceRefresh);
      const response = await Promise.race([backendUserPromise, timeoutPromise]) as { data: BackendUserData };
      
      console.log('🔍 Réponse brute du backend /users/me:', response);
      
      // ✨ response.data contient directement les données utilisateur
      const backendUser = mapBackendResponseToUser(response.data);
      
      console.log('✅ Profil mappé depuis le backend:', {
        id: backendUser.id,
        email: backendUser.email,
        name: backendUser.name,
        financialInstitutionId: backendUser.financialInstitutionId,
        companyId: backendUser.companyId,
        userType: backendUser.userType
      });
      
      // Vérifier si l'image du profil existe et est valide
      if (backendUser.picture) {
        try {
          // Vérifier si l'URL est valide
          new URL(backendUser.picture);
        } catch (e) {
          console.warn('URL de profil invalide dans les données backend, utilisation de Auth0', e);
          backendUser.picture = auth0User.picture || '';
        }
      }
      
      // Si le backend ne fournit pas de photo mais Auth0 oui, utiliser celle d'Auth0
      if (!backendUser.picture && auth0User.picture) {
        backendUser.picture = auth0User.picture;
      }
      
      // Fusionner les données avec priorité au backend pour la plupart des champs
      // mais en gardant certaines données Auth0 comme fallback
      const mergedUser = { 
        ...baseUser, 
        ...backendUser,
        // S'assurer que des champs critiques ne sont pas perdus ou vides
        id: backendUser.id || baseUser.id,
        name: backendUser.name || baseUser.name,
        email: backendUser.email || baseUser.email,
        picture: backendUser.picture || baseUser.picture,
        // S'assurer que le role est correctement extrait
        role: backendUser.role || baseUser.role
      };
      
      // Mettre à jour le localStorage seulement si on force le rafraîchissement
      // ou si les données ont changé de manière significative
      const shouldUpdateStorage = forceRefresh || 
        JSON.stringify({
          name: mergedUser.name,
          email: mergedUser.email,
          picture: mergedUser.picture,
          userType: mergedUser.userType
        }) !== JSON.stringify({
          name: baseUser.name,
          email: baseUser.email,
          picture: baseUser.picture,
          userType: baseUser.userType
        });
      
      if (shouldUpdateStorage) {
        localStorage.setItem('auth0_user', JSON.stringify(mergedUser));
      }
      
      return mergedUser;
    } catch (error) {
      console.warn('Backend user profile fetch failed. Using Auth0 data only.', error);
      // En cas d'erreur, on retourne quand même les données Auth0 pour ne pas bloquer l'app
      return baseUser;
    }
  }

  /**
   * Met à jour le profil de l'utilisateur.
   * Sauvegarde en localStorage et tente d'envoyer au backend si connecté.
   * @param data - Les données de l'utilisateur à mettre à jour.
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    console.log('Updating profile with:', data);
    
    // Sauvegarde locale d'abord (toujours disponible)
    const stored = localStorage.getItem('auth0_user');
    const currentUser = stored ? JSON.parse(stored) : {};
    const updatedUser = { ...currentUser, ...data };
    
    // S'assurer que les champs essentiels ne sont pas écrasés par des valeurs undefined
    if (data.picture === undefined && currentUser.picture) {
      updatedUser.picture = currentUser.picture;
    }
    if (data.name === undefined && currentUser.name) {
      updatedUser.name = currentUser.name;
    }
    
    // Sauvegarder en localStorage pour une disponibilité immédiate dans le Header
    localStorage.setItem('auth0_user', JSON.stringify(updatedUser));
    
    // Tenter d'envoyer au backend si connecté
    try {
      const token = getToken();
      if (token) {
        // Créer une promesse avec timeout pour éviter de bloquer l'UI
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Timeout')), API_TIMEOUT);
        });
        
        // Tenter d'envoyer au backend avec timeout
        const backendPromise = api.patch<User>('/users/me', data);
        const backendResponse = await Promise.race([backendPromise, timeoutPromise]) as User;
        
        console.log('Profile updated on backend:', backendResponse);
        
        // Fusion des données du backend avec les données locales
        const finalUser = { 
          ...updatedUser, 
          ...backendResponse,
          // S'assurer que les champs importants ne sont pas perdus
          name: backendResponse.name || updatedUser.name,
          picture: backendResponse.picture || updatedUser.picture,
          email: backendResponse.email || updatedUser.email
        };
        
        // Mettre à jour le localStorage avec les données finales
        localStorage.setItem('auth0_user', JSON.stringify(finalUser));
        
        // On retourne les données du backend qui sont plus à jour
        return finalUser;
      }
    } catch (error) {
      console.error('Failed to update profile on backend:', error);
      // En cas d'erreur, on continue avec les données locales
    }
    
    // Retourner les données locales mises à jour
    return updatedUser as User;
  }

  /**
   * Télécharge une nouvelle photo de profil via Cloudinary.
   * Flux: Fichier → Cloudinary → URL → Backend → localStorage
   * @param photoFile - Le fichier image à télécharger.
   */
  async uploadProfilePhoto(photoFile: File): Promise<{ url: string }> {
    console.log('📤 Upload photo via Cloudinary:', photoFile.name);
    
    // Étape 1: Upload sur Cloudinary
    const cloudinaryResult = await cloudinaryService.uploadAvatar(photoFile);
    const cloudinaryUrl = cloudinaryResult.url;
    console.log('✅ Photo uploadée sur Cloudinary:', cloudinaryUrl);
    
    // Étape 2: Mise à jour du localStorage avec l'URL Cloudinary
    const stored = localStorage.getItem('auth0_user');
    const currentUser = stored ? JSON.parse(stored) : {};
    currentUser.picture = cloudinaryUrl;
    localStorage.setItem('auth0_user', JSON.stringify(currentUser));
    
    // Étape 3: Envoyer l'URL au backend si connecté
    try {
      const token = getToken();
      if (token) {
        // Créer une promesse avec timeout pour éviter de bloquer l'UI
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Timeout')), API_TIMEOUT);
        });
        
        // Envoyer l'URL Cloudinary au backend (pas le fichier!)
        const updatePromise = api.patch<{ picture: string }>('/users/me', { picture: cloudinaryUrl });
        const response = await Promise.race([updatePromise, timeoutPromise]) as { picture: string };
        
        console.log('✅ URL photo mise à jour sur le backend:', response);
        
        // Mettre à jour le localStorage avec la réponse du backend si différente
        if (response.picture && response.picture !== cloudinaryUrl) {
          const updatedStoredData = localStorage.getItem('auth0_user');
          const updatedUser = updatedStoredData ? JSON.parse(updatedStoredData) : currentUser;
          updatedUser.picture = response.picture;
          localStorage.setItem('auth0_user', JSON.stringify(updatedUser));
          return { url: response.picture };
        }
      }
    } catch (error) {
      console.warn('⚠️ Échec mise à jour backend, utilisation URL Cloudinary:', error);
      // En cas d'erreur backend, l'URL Cloudinary reste valide
    }
    
    return { url: cloudinaryUrl };
  }
}

export const userService = new UserService();
